import React, { useRef } from 'react';
import { string, func, instanceOf, bool } from 'prop-types';
import DatePicker from 'react-datepicker';
import moment from 'moment';
import {
  START_DATE_DISABLED_STATUS,
  END_DATE_DISABLED_STATUS,
} from './DEConstants';

const CustomInput = React.memo(
  ({ value = '', onClick, onDateClick, disabled }) => {
    let dateValue = '';
    if (value && value !== 'Invalid date') {
      dateValue = value;
    }
    return (
      <button
        type="button"
        onClick={() => onDateClick(onClick)}
        className={`distro__date-picker--control
        ${disabled ? 'distro__date-picker--control-disabled' : ''}`}
      >
        {dateValue || (
          <span className="distro__date-picker--control-placeholder">
            mm/dd/yyyy
          </span>
        )}
      </button>
    );
  }
);

CustomInput.propTypes = {
  value: string,
  onClick: func,
  onDateClick: func,
  disabled: bool,
};

const CustomDateRangePicker = ({
  dateFormat,
  labelTo,
  labelFrom,
  handleDateChange,
  distRequest,
  distErrors,
  impersonated
}) => {
  const [focusOn, setFocus] = React.useState();
  const startDateNode = useRef();
  const endDateNode = useRef();
  const id = 'date';
  const reduxStartDate = distRequest.startDate
    ? new Date(distRequest.startDate)
    : null;
  const reduxEndDate = distRequest.endDate
    ? new Date(distRequest.endDate)
    : null;
  const onDateClick = (type, callback) => {
    setFocus(type);
    callback();
  };

  const removeFocus = () => {
    setFocus('');
  };

  const handleStartDate = startDate => {
    let isEndDateSameAsStartDate = false;
    const endDate = reduxEndDate;
    if (endDate && (startDate > endDate)) {
      isEndDateSameAsStartDate = true;
    }
    if (!endDate && startDateNode.current.calendar === undefined) {
      endDateNode.current.setFocus();
    } else {
      handleDateChange(
        startDate,
        isEndDateSameAsStartDate ? startDate : endDate
      );
    }
    removeFocus();
  };

  const handleEndDate = endDate => {
    let startDate = reduxStartDate;
    if (startDate && (endDate < startDate)) {
      startDate = endDate;
    }
    if (!startDate && endDateNode.current.calendar === undefined) {
      startDateNode.current.setFocus();
    } else {
      handleDateChange(startDate, endDate);
    }
    removeFocus();
  };

  let isStartDtDisabled = false;
  if (START_DATE_DISABLED_STATUS.includes(distRequest.status)) {
    isStartDtDisabled = true;
  }
  let isEndDtDisabled = false;
  if (END_DATE_DISABLED_STATUS.includes(distRequest.status)) {
    isEndDtDisabled = true;
  }

  const isError = distErrors.isDateErr && distErrors.isDateErrMsg;

  let startDateColClass = 'distro__date-picker--col';
  if (!reduxStartDate && distErrors.isDateErr) {
    startDateColClass += ' distro__date-picker--col--error';
  }
  if (!distRequest.isForActivation || isStartDtDisabled) {
    if (reduxStartDate) {
      startDateColClass += ' distro__date-picker--disabled--with-value';
    } else {
      startDateColClass += ' distro__date-picker--disabled';
    }
  }

  let endDateColClass = 'distro__date-picker--col';
  if (!reduxStartDate && distErrors.isDateErr) {
    endDateColClass += ' distro__date-picker--col--error';
  }
  if (!distRequest.isForActivation || isEndDtDisabled) {
    if (reduxStartDate) {
      endDateColClass += ' distro__date-picker--disabled--with-value';
    } else {
      endDateColClass += ' distro__date-picker--disabled';
    }
  }

  if (focusOn === 'startDate') {
    startDateColClass += ' distro__date-picker--col--focus';
  }
  if (focusOn === 'endDate') {
    endDateColClass += ' distro__date-picker--col--focus';
  }
  return (
    <div className="custom-date-picker">
      <div
        className={`distro__date-picker--wrapper ${isError ? 'error' : ' '}`}
      >
        <div className={startDateColClass}>
          <div className="custom-date-picker--element">
            {labelFrom && (
              <label
                className="distro__date-selector--label"
                htmlFor={`${id}_from`}
              >
                {labelFrom}
              </label>
            )}
            <DatePicker
              selected={reduxStartDate}
              selectsStart
              onChange={date => handleStartDate(date)}
              disabled={!distRequest.isForActivation || isStartDtDisabled || impersonated}
              placeholderText="mm/dd/yyyy"
              dateFormat={dateFormat}
              id={`${id}_from`}
              name={`${id}_from`}
              className="distro__date-picker--control"
              minDate={new Date(moment().add(3, 'days'))}
              startDate={reduxStartDate}
              ref={startDateNode}
              onClickOutside={() => removeFocus()}
              customInput={
                <CustomInput
                  disabled={!distRequest.isForActivation}
                  onDateClick={event => onDateClick('startDate', event)}
                />
              }
            />
          </div>
        </div>
        <div className={endDateColClass}>
          <div className="custom-date-picker--element">
            {labelTo && (
              <label
                className="distro__date-selector--label"
                htmlFor={`${id}_from`}
              >
                {labelTo}
              </label>
            )}
            <DatePicker
              selected={reduxEndDate}
              selectsEnd
              onChange={date => handleEndDate(date)}
              disabled={!distRequest.isForActivation || isEndDtDisabled || impersonated}
              placeholderText="mm/dd/yyyy"
              dateFormat={dateFormat}
              id={`${id}_to`}
              name={`${id}_to`}
              className="distro__date-picker--control"
              minDate={reduxStartDate || new Date(moment().add(3, 'days'))}
              ref={endDateNode}
              onClickOutside={() => removeFocus()}
              customInput={
                <CustomInput
                  disabled={!distRequest.isForActivation}
                  onDateClick={event => onDateClick('endDate', event)}
                />
              }
            />
          </div>
        </div>
      </div>
    </div>
  );
};

CustomDateRangePicker.propTypes = {
  dateFormat: string,
  labelTo: string,
  labelFrom: string,
  handleDateChange: func,
  distRequest: instanceOf(Object),
  distErrors: instanceOf(Object),
  impersonated: bool,
};

export default CustomDateRangePicker;
